"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Zap, Weight, Ruler, Gauge, Activity, CircleCheck } from "lucide-react";
export default function TheBeastOfTheBrush() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const imageRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top;
        const windowHeight = window.innerHeight;
        if (sectionTop < windowHeight && rect.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionRef]);
  const getTransform = () => {
    if (!sectionRef.current || typeof window === 'undefined') return "scale(1) translateY(0)";
    const rect = sectionRef.current.getBoundingClientRect();
    const viewHeight = window.innerHeight;
    const progress = 1 - rect.bottom / (rect.height + viewHeight);
    const safeProgress = Math.max(0, Math.min(1, progress));
    const scale = 0.95 + safeProgress * 0.15;
    const translateY = safeProgress * -30;
    return `scale(${scale}) translateY(${translateY}px)`;
  };
  const specs = [{
    label: "Engine Power",
    value: "96.4 HP",
    icon: Zap
  }, {
    label: "Hydraulic Flow",
    value: "40.0 GPM",
    icon: Gauge
  }, {
    label: "Tipping Load",
    value: "9,620 lbs",
    icon: Weight
  }, {
    label: "Mulching Width",
    value: "72 Inches",
    icon: Ruler
  }, {
    label: "Travel Speed",
    value: "7.3 MPH",
    icon: Activity
  }];
  const features = ["High-Flow Auxiliary Hydraulics", "Forestry Door & Guarding", "Vertical Lift Path Loader", "Wide Track System"];
  return <section id="the-beast-of-the-brush" ref={sectionRef} className="relative w-full overflow-hidden bg-background text-foreground dark py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1/2 h-full bg-gradient-to-r from-background to-transparent z-10" />
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_50%,hsla(var(--primary),0.05),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-8 items-center">
          
          {}
          <div className="lg:col-span-3 relative flex justify-center lg:justify-start order-2 lg:order-1">
            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-primary/20 blur-[80px] rounded-full pointer-events-none" />
            
            {}
            <div ref={imageRef} className="relative z-10 w-full max-w-2xl transition-transform duration-100 ease-out will-change-transform" style={{
            transform: getTransform()
          }}>
              <img src="https://picked-bream.10web.cloud/wp-content/uploads/2026/03/kubota-SVL97-3-image.jpg" alt="Kubota SVL97-3 Compact Track Loader with Forestry Mulcher" className="w-full h-auto drop-shadow-2xl object-contain" />
            </div>
          </div>

          {}
          <div className={`lg:col-span-2 order-1 lg:order-2 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <Card className="bg-card/60 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
              <CardHeader className="pb-4 border-b border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="border-primary/50 text-primary bg-primary/10 px-3 py-1 text-[10px] tracking-widest uppercase">
                    Operational Centerpiece
                  </Badge>
                </div>
                <CardTitle className="font-serif text-4xl md:text-5xl lg:text-6xl uppercase tracking-tighter text-foreground leading-[0.9]">
                  The Beast <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-600">
                    Of The Brush
                  </span>
                </CardTitle>
                <CardDescription className="text-lg pt-2 text-muted-foreground">
                  The Kubota SVL97-3 is engineered for extreme performance in the toughest East Texas terrain.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-6 space-y-8">
                {}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {specs.map((spec, i) => <div key={spec.label} data-index={i} className="flex items-center gap-3 p-3 rounded-lg bg-background/40 border border-white/5 hover:border-primary/30 transition-colors group">
                      <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <spec.icon className="size-5" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{spec.label}</p>
                        <p className="text-lg font-bold font-mono text-foreground">{spec.value}</p>
                      </div>
                    </div>)}
                </div>

                {}
                <div className="space-y-3 pt-2">
                  <p className="text-sm font-semibold text-foreground uppercase tracking-widest mb-2">
                    Built for Forestry
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {features.map((feature, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CircleCheck className="size-4 text-primary shrink-0" />
                        <span>{feature}</span>
                      </div>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}